import React from 'react';
import TermsLayout from './TermsLayout';

function DeleteAccount() {
  return (
    <TermsLayout>
      <h1>Delete Account</h1>
      <p className="last-updated">Last Updated: June 2026</p>

      <p>
        You can request deletion of your Zella account and associated personal data at any time, free of charge. This page explains what is deleted, what is retained, why, and how to make the request.
      </p>

      <h2>Deleting an Employee Account</h2>
      <p>The quickest way to delete your employee account is directly from the Zella mobile app:</p>
      <ol>
        <li>Open the Zella mobile app</li>
        <li>Tap the menu icon (☰) in the top corner</li>
        <li>Select <strong>Settings</strong></li>
        <li>Select <strong>Delete Account</strong></li>
        <li>Confirm deletion when prompted</li>
      </ol>
      <p>
        If you cannot access the app, email{' '}
        <a href="mailto:privacy@zellawellbeing.com">privacy@zellawellbeing.com</a>{' '}
        with subject "Account Deletion Request" and we will process the request manually within 30 days.
      </p>
      <p>
        <strong>What is deleted:</strong> Your email address, display name, password credentials (from Firebase Authentication), team membership, company association, account status records, and all Firebase Authentication data associated with your account (including login timestamps and custom claims) will be permanently deleted within 30 days.
      </p>
      <p>
        <strong>A note on survey records:</strong> Survey records in our database contain your user ID (uid) as a technical field. When your account is deleted, your account record — which links your uid to your name and email — is removed. The uid value may persist in historical survey records as a technical artefact, but because the account no longer exists, your identity cannot be derived from it. Zella does not grant administrators or third parties access to the surveys collection, and no product interface surfaces individual survey records. If you would like us to delete the survey records associated with your uid as well, include this request in your email and we will process it within 30 days.
      </p>
      <p>
        <strong>This action cannot be undone.</strong>
      </p>

      <h2>Deleting an Administrator Account</h2>
      <p>Administrator accounts can be deleted from the Zella web dashboard:</p>
      <ol>
        <li>Log in to your dashboard at <a href="https://zellawellbeing.com">zellawellbeing.com</a></li>
        <li>Go to <strong>Account Settings</strong></li>
        <li>Select <strong>Delete Account</strong></li>
        <li>Confirm deletion when prompted</li>
      </ol>
      <p>
        Deleting an administrator account closes your organisation's Zella account entirely. All administrator and employee account records, team structure, and company data will be permanently deleted within 30 days.
      </p>
      <p>
        <strong>Survey data:</strong> Aggregated weekly team averages (the teamAverages collection) and individual survey records (the surveys collection) associated with your organisation will lose their company identifier. They will no longer be linked to your organisation and will be retained only as industry and regional level data. No personal employee data remains in these records after account deletion.
      </p>
      <p>
        <strong>Billing:</strong> If your organisation has active scheduled payments, these will be cancelled immediately. No refund is provided for any unused portion of a paid billing period. Billing records are retained for 7 years as required by HMRC (see below).
      </p>
      <p>
        <strong>Employee notification:</strong> We recommend notifying your employees before deleting your organisation account, as their employee accounts will also be deactivated and they will lose access to the app.
      </p>
      <p>
        <strong>This action cannot be undone.</strong>
      </p>

      <h2>What Data Is Deleted</h2>
      <p>Upon account deletion, the following personal data is permanently removed within 30 days:</p>
      <ul>
        <li>Email address and password credentials (Firebase Authentication)</li>
        <li>Display name and profile information</li>
        <li>Firebase Authentication records (uid, login timestamps, custom claims)</li>
        <li>Team membership records and approval status (employee accounts)</li>
        <li>Company association records</li>
        <li>All company, team, and employee records (administrator account deletion)</li>
        <li>Subscription and scheduled payment configuration (administrator accounts)</li>
        <li>Feedback and bug reports submitted under the account</li>
        <li>Survey records associated with your uid, on specific request</li>
      </ul>

      <h2>What Data Is Retained and Why</h2>
      <ul>
        <li>
          <strong>Survey records (uid field):</strong> Individual survey records may retain your uid as a technical artefact after account deletion. Your account record — which links uid to your identity — is deleted, making re-identification impossible. On specific request, we will delete survey records associated with your uid within 30 days.
        </li>
        <li>
          <strong>Aggregated team averages:</strong> Weekly team average records (average scores per team per week) contain no personal identifiers and are retained as operational and research data.
        </li>
        <li>
          <strong>Billing and payment records:</strong> Retained for 7 years from the date of each transaction, as required by HMRC for tax and accounting compliance. These records include transaction amounts, dates, and Stripe-generated identifiers — not full card details.
        </li>
      </ul>

      <h2>Cancelling a Subscription Without Deleting Your Account</h2>
      <p>
        If you want to stop paying but keep your account, you can cancel individual scheduled payments from the <strong>Billing</strong> section of the web dashboard. Access to paid features continues until the end of the period already paid for. When that period ends, your employee limit drops to the free tier (10 employees). Your account, data, and team structure are not affected.
      </p>

      <h2>Your Right to Deletion Under UK GDPR</h2>
      <p>
        Under Article 17 of UK GDPR, you have the right to request erasure of your personal data. Zella will honour this right in full for all personal account data. The retention of billing records for 7 years is a legal obligation under HMRC rules that overrides the right to erasure for those specific records (Article 17(3)(b) — retention required to comply with a legal obligation). The retention of aggregated, non-identifiable survey averages is not subject to the right to erasure as they do not constitute personal data at the point of the request.
      </p>

      <h2>Response Times</h2>
      <p>
        Account deletions requested through the app or dashboard are initiated immediately. Full deletion of all associated records is completed within 30 days. Manual deletion requests submitted by email are also processed within 30 days of receipt.
      </p>
      <p>
        If we require identity verification before processing a manual deletion request, we will contact you promptly and the 30-day period begins from the date we verify your identity.
      </p>

      <h2>Need Help?</h2>
      <p>
        For any issues with account deletion, manual deletion requests, or questions about what data is held:
      </p>
      <p>
        Email: <a href="mailto:privacy@zellawellbeing.com">privacy@zellawellbeing.com</a><br />
        Subject: "Account Deletion Request"
      </p>
      <p>
        For urgent deletion requests (suspected data breach or unauthorised access to your account):
      </p>
      <p>
        Email: <a href="mailto:privacy@zellawellbeing.com">privacy@zellawellbeing.com</a><br />
        Subject: "URGENT — Account Deletion"<br />
        We aim to respond within 72 hours.
      </p>
    </TermsLayout>
  );
}

export default DeleteAccount;